import React, { useEffect } from "react";
import AsyncSelect from "react-select/async";
import { useLazySearchCustomersAllBranchesQuery } from "../../masters/customers/customersApi";

const toOption = (c) => ({
  value: `${c.branchId}-${c.custId}`,
  label: `${c.custName} · ${c.branchCode}${c.contactPerson ? ` · ${c.contactPerson}` : ""}`,
  customer: c,
});

// The shared existing-customer picker. Used by ~11 screens now (Enquiry,
// Quotation, Log Complaint, Collections, Guard Lookup, Dispatch Alarm, Stock
// Movement, Reconciliation, One Stop, Alarm Response, Client Visits).
//
// NOT cross-branch, despite the endpoint's name. It calls
// /masters/customers/search-all-branches, which since 2026-09-18 is
// branch-scoped server-side to the caller's own branches - it used to return
// every branch's customers to any logged-in account, which is how a TexasTrack
// user came to see Mombasa customers in the Log Complaint picker. The route
// keeps its old name for compatibility; `crossBranch: true` is the opt-in for
// the duplicate check and link panel, and must NOT be added here.
//
// Still needs no permission of its own, deliberately - picking a customer is
// part of the job that needs it, not a separate right (2026-09-09). Only custId/branchId are needed by
// CreateSalesEnquiryRequest (CustomerName/Phone/Email/Address are resolved
// server-side from the link) - name/branch/contact shown here purely to
// help the salesperson pick the right one.
// `isClearable` defaults to false (unchanged behavior for every existing
// caller - Enquiry/LPO forms treat "linked to an existing customer" as a
// deliberate toggle, not something to blank back out via an inline x) -
// pass true explicitly for a picker used as a plain filter (e.g. Stock
// Movement Report), where clearing the selection back to "no filter" needs
// its own visible affordance.
// `branchId` (optional, 2026-10-02): keep the results to that one branch - for a
// picker used as a filter on a page that follows the header branch. When it
// changes, a picked customer of another branch is cleared (onChange(null)), so
// the page's filter never points at a customer outside the branch on screen.
// Omitted/null = every branch the caller has, as before.
const CustomerPicker = ({ value, onChange, menuPortalTarget, isClearable = false, branchId = null }) => {
  const [triggerSearch] = useLazySearchCustomersAllBranchesQuery();

  useEffect(() => {
    if (branchId != null && value?.customer && value.customer.branchId !== branchId) onChange(null);
  }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) return [];
    const { data = [] } = await triggerSearch(inputValue.trim());
    return data.filter((c) => branchId == null || c.branchId === branchId).map(toOption);
  };

  return (
    <AsyncSelect
      classNamePrefix="react-select"
      cacheOptions={branchId ?? "all"}
      isClearable={isClearable}
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      placeholder="Search existing customer by name..."
      noOptionsMessage={({ inputValue }) => (inputValue ? "No matching customers" : "Type at least 2 characters")}
      menuPortalTarget={menuPortalTarget}
      styles={menuPortalTarget ? { menuPortal: (base) => ({ ...base, zIndex: 9999 }) } : undefined}
    />
  );
};

export default CustomerPicker;
